/**
 * File: de_form2.js - Contains the logic for submitting a digital
 * enhancenment
 * Copyright: (c) Copyright December 2019 by InBrace
 * Authors: David Vu
 * Project: InBrace Provider/Business Portal
 * Special Notes: NA
 **/
// ---------------------------------- Imports ----------------------------------
// External Libs
import _ from 'lodash';
import Moment from 'moment';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { isValidDate } from '../../common/helpers';
import * as nomenclature from '../../common/nomenclature';
import { buildRecordStates, onLoadImageRecord, onRecordViewerAction } from '../../redux/actions/record_viewer/record_viewer';
import { onImageToolAction } from '../../common/functions';
import { getRecordStates } from '../../redux/reducers/record_viewer/record_viewer';
import AlertContainer from '../components/container/alert_container';
import CardContainer from '../components/container/card_container';
import TextArea from '../components/inputs/text_area';
import TextBox from '../components/inputs/text_box';
import RecordViewer from '../components/record_viewer/record_viewer';
import { getMinDate, getMaxDate } from '../../common/helpers';
import { ToothViewer, Teeth } from '../case_submission/tx_plan';
import Dropdown from '../components/inputs/dropdown';
import { InfoBlueIcon } from '../assets/icons';

/**
 * Renders a teeth selector chart for a specific field in the form.
 * @function
 * @param {Object} props - The props object.
 * @param {string} props.field - The name of the field.
 * @param {boolean} props.hasUpper - Whether the patient has upper teeth.
 * @param {boolean} props.hasLower - Whether the patient has lower teeth.
 * @param {Array<string>} props.disabledTeeth - An array of teeth to disable.
 * @param {Array<string>} props.teeth - An array of selected teeth.
 * @param {Object} props.teethReference - An object containing arrays of teeth for each quadrant.
 * @param {number} props.minPreset - The minimum preset size.
 * @param {number} props.maxPreset - The maximum preset size.
 * @param {Array<Array<number>>} props.presets - An array of preset tooth configurations.
 * @param {Object} props.tset - The bracket set object.
 * @param {string} props.dataTestId - The data-testid attribute for testing.
 * @param {string} props.heading - The heading for the field.
 * @param {JSX.Element} props.headingMessage - The heading message for the field.
 * @param {string} props.sub_heading - The sub-heading for the field.
 * @param {string} props.footerNote - The footer note for the field.
 * @param {string} props.warning - The warning message for the field.
 * @param {boolean} props.showPresets - Whether to show the preset buttons.
 * @param {number} props.headingIndex - The index for the heading.
 * @param {function} props.onClick - The function to call when a tooth is clicked.
 * @param {function} props.setBracketTeeth - The function to call when a preset is selected.
 * @returns {JSX.Element} - The teeth selector chart as a JSX element.
 */
function TeethArea(props) {
  let disabledTeeth = [];

  if (['de4_bracket_teeth', 'de4_teeth_restriction'].includes(props.field)) {
    if (!props.hasUpper) {
      disabledTeeth = disabledTeeth.concat(props.teethReference.UR.concat(props.teethReference.UL));
    }
    if (!props.hasLower) {
      disabledTeeth = disabledTeeth.concat(props.teethReference.LR.concat(props.teethReference.LL));
    }
  }

  if (props.disabledTeeth) {
    disabledTeeth = disabledTeeth.concat(props.disabledTeeth);
  }

  /**
   * Constructs teeth selector chart
   * @function
   * @param {Array<string>} teethSet - Set of teeth to display
   * @return {Array<JSX.Element>} - Array of JSX elements for each tooth
   */
  const teethArray = (teethSet) => {
    return teethSet.map((teeth, ind) => {
      if (disabledTeeth.includes(teeth)) {
        return (
          <button key={ind} type="button" className="btn-ib btn-ib--disabled">
            {teeth}
          </button>
        );
      }

      let btnClassName = 'btn-ib';
      if (props.field === 'de4_bracket_teeth') {
        if (props.teeth.includes(teeth)) {
          if (props.prevBrackets?.includes(teeth)) btnClassName += ' btn-ib--active';
          else btnClassName += ' btn-ib--new';
        } else {
          if (props.prevBrackets?.includes(teeth)) btnClassName += ' btn-ib--removed';
        }
      } else if (props.teeth.includes(teeth)) {
        btnClassName += ' btn-ib--active';
      }

      return (
        <button key={ind} type="button" className={btnClassName} data-field={props.field} onClick={props.onClick} value={teeth}>
          {teeth}
        </button>
      );
    });
  };

  /**
   * Checks if the given teeth match the preset for the given index.
   * @function
   * @param {number} n - The index of the preset to check.
   * @param {Array<string>} teeth - The teeth to check against the preset.
   * @param {Object} tset - The bracket set object.
   * @returns {boolean} - True if the teeth match the preset, false otherwise.
   */
  const checkPreset = (n, teeth, tset) => {
    let diff = _.xor(props.presets[n], teeth);
    diff = _.difference(diff, tset.bracketDisabled);
    return diff.length === 0;
  };

  const presetChoice = _.range(props.minPreset, props.maxPreset + 1).find((n) => {
    return checkPreset(n, props.de4_bracket_teeth, props.tset);
  });

  let presetMax = _.range(props.minPreset, props.maxPreset + 1).find((n) => {
    return checkPreset(n, Teeth.All, props.tset);
  });

  presetMax = presetMax ? presetMax : props.maxPreset + 1;

  /**
   * Creates a preset button with the given size.
   * @function
   * @param {number} n - The size of the preset button.
   * @returns {JSX.Element} - The preset button as a JSX element.
   */
  const makePresetButton = (n) => {
    return (
      <button
        key={n}
        type="button"
        onClick={() => props.setBracketTeeth(props.presets[n])}
        className={'btn-ib ' + (n === presetChoice ? 'btn-ib--active ' : '') + (n > presetMax ? 'btn-ib--disabled' : '')}
      >
        {n}x{n}
      </button>
    );
  };

  return (
    <div className="question-box" data-testid={props.dataTestId}>
      <span id={props.field} className="title emphasis">
        {props.headingIndex ? <span className="title-index emphasis">{props.headingIndex}.</span> : null}
        {props.heading}
      </span>
      {props.headingMessage && <div className="question-box__heading-message">{props.headingMessage}</div>}
      <CardContainer type="form">
        {props.sub_heading ? props.sub_heading : null}
        {props.showPresets && (
          <div className="bracket-preset">
            Presets:{' '}
            <div className="btn-bar" role="toolbar" id="bracket-presets">
              {_.range(props.minPreset, props.maxPreset + 1)
                .reverse()
                .map((n) => makePresetButton(n))}
            </div>
          </div>
        )}
        <div className="teeth-selector">
          <div className="row">
            <div className="col-md-6">{teethArray(props.teethReference.UR)}</div>
            <div className="col-md-6">{teethArray(props.teethReference.UL)}</div>
          </div>
          <div className="row">
            <div className="col-md-6">{teethArray(props.teethReference.LR)}</div>
            <div className="col-md-6">{teethArray(props.teethReference.LL)}</div>
          </div>
        </div>
        {props.footerNote && props.footerNote}
        {presetChoice === 4 && (
          <AlertContainer>
            4x4 treatment is designed for limited cases and offered at a reduced base lab fee. Refinement Smartwires are not included and will be billed
            separately. For full details, please refer to the InSmile Braces Pricing Guide in the Knowledge Base.
          </AlertContainer>
        )}
        {props.warning ? props.warning : null}
      </CardContainer>
    </div>
  );
}

/**
 * Renders a component with a date input and a label for wire insertion date.
 * @param {Object} props - The props object containing the following properties:
 * @param {string} props.dataTestId - The data-testid attribute for testing purposes.
 * @param {string} props.field - The name of the field.
 * @param {string} props.value - The value of the date input.
 * @param {function} props.onChange - The function to be called when the date input changes.
 * @param {function} props.onFocus - The function to be called when the date input is focused.
 * @param {boolean} props.warning - A flag indicating whether the date input is invalid.
 * @returns {JSX.Element} - A JSX element containing a date input and a label for wire insertion date.
 */
function RadioDate(props) {
  return (
    <div data-testid={props.dataTestId}>
      <label className={props.warning ? 'inline-date-container--warning' : ''}>Date of wire insertion:</label>
      <TextBox
        id={props.field + '_input'}
        type="date"
        data-field={props.field}
        value={props.value}
        onChange={props.onChange}
        onFocus={props.onFocus}
        className={props.warning ? 'warning-border' : ''}
        max={getMaxDate()}
        min={getMinDate()}
      />
      {props.warning && <span className="title title--warning emphasis">Invalid Date</span>}
    </div>
  );
}

class DigitalEnhancementForm4 extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      thumbs: [],
    };
  }

  componentDidMount() {
    this.buildImageRecords();
    if (this.props.setFormVisited) this.props.setFormVisited();
  }

  componentDidUpdate(prevProps) {
    if (this.props.photoUpload !== prevProps.photoUpload || this.props.xrayUpload !== prevProps.xrayUpload || this.props.scanUpload !== prevProps.scanUpload) {
      this.buildImageRecords();
    }
  }

  buildImageRecords() {
    const {
      photoUpload,
      xrayUpload,
      scanUpload,
      // iTero integration
      uploadMethod,
      selectedIteroScanRecords,
    } = this.props;
    const records = [];
    if (photoUpload) records.push(...photoUpload);
    if (xrayUpload) records.push(...xrayUpload);

    // iTero integration
    if (uploadMethod === 'itero' && selectedIteroScanRecords) {
      records.push(...selectedIteroScanRecords);
    } else if (scanUpload) {
      records.push(...scanUpload);
    }

    const options = { teethViewer: true, caseType: 'complete' };
    this.props.buildRecordStates(records, options);
  }

  upper_smartwire_field = 'de4_upper_smartwire';
  lower_smartwire_field = 'de4_lower_smartwire';

  upper_smartwire_gen2 = [
    {
      label: nomenclature.SMARTWIRE_0,
      field: this.upper_smartwire_field,
      value: 'stage0',
      selection: () => this.props.de4_upper_smartwire,
      onChange: this.props.onInputChange_de4,
      onFocus: this.props.onFocus,
      date: true,
      date_field: this.upper_smartwire_field + '_date',
      date_value: () => this.props.de4_upper_smartwire_date,
      warning: () => !isValidDate(this.props.de4_upper_smartwire_date),
    },
    {
      label: nomenclature.SMARTWIRE_1,
      field: this.upper_smartwire_field,
      value: 'stage1',
      selection: () => this.props.de4_upper_smartwire,
      onChange: this.props.onInputChange_de4,
      onFocus: this.props.onFocus,
      date: true,
      date_field: this.upper_smartwire_field + '_date',
      date_value: () => this.props.de4_upper_smartwire_date,
      warning: () => !isValidDate(this.props.de4_upper_smartwire_date),
    },
    {
      label: nomenclature.SMARTWIRE_2,
      field: this.upper_smartwire_field,
      value: 'stage2',
      selection: () => this.props.de4_upper_smartwire,
      onChange: this.props.onInputChange_de4,
      onFocus: this.props.onFocus,
      date: true,
      date_field: this.upper_smartwire_field + '_date',
      date_value: () => this.props.de4_upper_smartwire_date,
      warning: () => !isValidDate(this.props.de4_upper_smartwire_date),
    },
    {
      label: nomenclature.SMARTWIRE_3,
      field: this.upper_smartwire_field,
      value: 'stage3',
      selection: () => this.props.de4_upper_smartwire,
      onChange: this.props.onInputChange_de4,
      onFocus: this.props.onFocus,
      date: true,
      date_field: this.upper_smartwire_field + '_date',
      date_value: () => this.props.de4_upper_smartwire_date,
      warning: () => !isValidDate(this.props.de4_upper_smartwire_date),
    },
    {
      label: 'None',
      field: this.upper_smartwire_field,
      value: 'none',
      selection: () => this.props.de4_upper_smartwire,
      onChange: this.props.onInputChange_de4,
      onFocus: this.props.onFocus,
      date: false,
      warning: () => false,
    },
  ];

  lower_smartwire_gen2 = [
    {
      label: nomenclature.SMARTWIRE_0,
      field: this.lower_smartwire_field,
      value: 'stage0',
      selection: () => this.props.de4_lower_smartwire,
      onChange: this.props.onInputChange_de4,
      onFocus: this.props.onFocus,
      date: true,
      date_field: this.lower_smartwire_field + '_date',
      date_value: () => this.props.de4_lower_smartwire_date,
      warning: () => !isValidDate(this.props.de4_lower_smartwire_date),
    },
    {
      label: nomenclature.SMARTWIRE_1,
      field: this.lower_smartwire_field,
      value: 'stage1',
      selection: () => this.props.de4_lower_smartwire,
      onChange: this.props.onInputChange_de4,
      onFocus: this.props.onFocus,
      date: true,
      date_field: this.lower_smartwire_field + '_date',
      date_value: () => this.props.de4_lower_smartwire_date,
      warning: () => !isValidDate(this.props.de4_lower_smartwire_date),
    },
    {
      label: nomenclature.SMARTWIRE_2,
      field: this.lower_smartwire_field,
      value: 'stage2',
      selection: () => this.props.de4_lower_smartwire,
      onChange: this.props.onInputChange_de4,
      onFocus: this.props.onFocus,
      date: true,
      date_field: this.lower_smartwire_field + '_date',
      date_value: () => this.props.de4_lower_smartwire_date,
      warning: () => !isValidDate(this.props.de4_lower_smartwire_date),
    },
    {
      label: nomenclature.SMARTWIRE_3,
      field: this.lower_smartwire_field,
      value: 'stage3',
      selection: () => this.props.de4_lower_smartwire,
      onChange: this.props.onInputChange_de4,
      onFocus: this.props.onFocus,
      date: true,
      date_field: this.lower_smartwire_field + '_date',
      date_value: () => this.props.de4_lower_smartwire_date,
      warning: () => !isValidDate(this.props.de4_lower_smartwire_date),
    },
    {
      label: 'None',
      field: this.lower_smartwire_field,
      value: 'none',
      selection: () => this.props.de4_lower_smartwire,
      onChange: this.props.onInputChange_de4,
      onFocus: this.props.onFocus,
      date: false,
      warning: () => false,
    },
  ];

  isDe = () => {
    return this.props && this.props.de4_prev_case_info && this.props.de4_prev_case_info.case_id && this.props.de4_prev_case_info.case_id.indexOf('-') >= 0;
  };

  getRestrictionMsg = () => {
    let result = '';

    if (
      this.props.de4_prev_case_info &&
      this.props.de4_prev_case_info.exist &&
      _.difference(this.props.de4_teeth_restriction, this.props.de4_prev_case_info.teeth_restriction).length === 0 &&
      _.difference(this.props.de4_prev_case_info.teeth_restriction, this.props.de4_teeth_restriction).length === 0
    ) {
      result = 'Teeth indicated not to be moved from your most recent Case Design have been preselected for you';
    }

    return result;
  };

  smartWireWarning = (warning_msg) => {
    if (warning_msg) {
      return (
        <div className="col-md-12">
          <AlertContainer>
            {warning_msg.split('.').map((sentence, ind) => {
              return (
                <span key={ind}>
                  {sentence}
                  <br />
                </span>
              );
            })}
          </AlertContainer>
        </div>
      );
    }

    return null;
  };

  smartWireWornLessThan6Weeks = (date) => {
    let less_than_6_weeks = false;

    if (date) {
      const days = Moment().diff(Moment(date), 'days');
      const six_weeks_in_days = 6 * 7;

      less_than_6_weeks = days < six_weeks_in_days;
    }

    return less_than_6_weeks;
  };

  showSmartWireDateWarning = () => {
    // Standard warning for stage 1 wire
    let warning_msg = '';
    const upper_smartwire = this.props.de4_upper_smartwire;
    const upper_smartwire_date = this.props.de4_upper_smartwire_date;
    const lower_smartwire = this.props.de4_lower_smartwire;
    const lower_smartwire_date = this.props.de4_lower_smartwire_date;

    if (upper_smartwire === 'stage1' || lower_smartwire === 'stage1') {
      warning_msg = `Please insert the ${nomenclature.SMARTWIRE_2} and allow it to fully express before submitting for Case Design`;
    }

    if (
      (upper_smartwire !== 'none' && upper_smartwire !== 'stage1' && upper_smartwire_date !== '' && this.smartWireWornLessThan6Weeks(upper_smartwire_date)) ||
      (lower_smartwire !== 'none' &&
        lower_smartwire !== 'stage1' &&
        lower_smartwire !== 'stage2a' &&
        lower_smartwire !== 'de_16_straight' &&
        lower_smartwire_date !== '' &&
        this.smartWireWornLessThan6Weeks(lower_smartwire_date))
    ) {
      if (warning_msg !== '') {
        warning_msg = warning_msg + '.';
      }
      warning_msg = warning_msg + 'Please ensure there has been no observed tooth movement for the last 4-8 weeks';
    }

    if (!this.props.reupload) {
      if (warning_msg) {
        this.props.getDeHold(true);
      } else {
        this.props.getDeHold(false);
      }
    }

    return this.smartWireWarning(warning_msg);
  };

  getFormClass() {
    return this.props.record_states.length > 0 ? 'col-md-8' : 'col-md-12';
  }

  displaySmileDesignTooltip(id) {
    $(`#${id}`).tooltip('show');
  }

  hideSmileDesignTooltip(id) {
    $(`#${id}`).tooltip('hide');
  }

  getSmartwireOptions(arch) {
    const options = arch === 'upper' ? this.upper_smartwire_gen2 : this.lower_smartwire_gen2;
    const { prevWireSequences = [] } = this.props;

    const archSeqs = prevWireSequences.filter((ws) => ws.wire_name.includes(arch));
    if (!archSeqs.length) return options;

    const byValue = new Map(options.map((o) => [o.value, o]));
    const noneOption = byValue.get('none');

    const wireVerbiage = {
      specialty: 'stage0',
      initial: 'stage1',
      intermediate: 'stage2',
      final: 'stage3',
    };

    return [
      noneOption,
      ...archSeqs
        .map((seq) => {
          const wireType = seq.wire_name.split('_')[1];
          const value = wireVerbiage[wireType];
          const base = byValue.get(value);
          return base ? { ...base, label: `Smartwire ${seq.sequence}` } : null;
        })
        .filter(Boolean),
    ];
  }

  canDisableDropdown() {
    if (this.props.isDraft) {
      return !this.props.isWipSwSeq;
    }
    return this.props.prevWireSequences?.length > 0;
  }

  getBracketsHeadingMessage() {
    return (
      <>
        <div>
          <span className="bracket-label bracket-label--selected">Blue</span> box indicates the current bracket placement. Any missing bracket in a{' '}
          <span className="bracket-label bracket-label--selected">Blue</span> box will be replaced with an IDB tray.
        </div>
        <div>
          Click <span className="bracket-label bracket-label--selected">Blue</span> box to remove a bracket (<span className="bracket-text-remove">red</span>).
        </div>
        <div>
          Click <span className="bracket-label bracket-label--unselected">White</span> box to add a bracket (<span className="bracket-text-add">green</span>).
        </div>
      </>
    );
  }

  render() {
    const tset = this.props.getImpliedTeethSets();
    const lowerSmartwireOptions = this.getSmartwireOptions('lower');
    const upperSmartwireOptions = this.getSmartwireOptions('upper');
    return (
      <div className="row">
        <form className={this.getFormClass()}>
          {/* 1. Arch to treat */}
          <div className="row">
            <div className="col-md-12">
              <div className="question-box" data-testid="de4_arch_to_treat_box">
                <span id="de4_arch_to_treat" className="title emphasis">
                  <span className="title-index emphasis">1.</span>
                  Arch to treat
                </span>

                <CardContainer type="form">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="ib-radio">
                        <label className="form-check-label ">
                          <input
                            id="de4_arch_to_treat-upper"
                            className="form-check-input"
                            type="checkbox"
                            name="de4_arch_to_treat"
                            data-field="de4_arch_to_treat"
                            value="upper"
                            onChange={this.props.onInputChange_de4}
                            checked={['upper', 'both'].includes(this.props.de4_arch_to_treat)}
                            defaultChecked={this.props.prevArches.includes('upper')}
                          />{' '}
                          Upper arch - {this.props.de4_is_upper_smartwire_new ? 'Initial' : 'Additional'} Smartwires
                        </label>{' '}
                      </div>
                      <div className="ib-radio">
                        <label className="form-check-label ">
                          <input
                            id="de4_arch_to_treat-lower"
                            className="form-check-input"
                            type="checkbox"
                            name="de4_arch_to_treat"
                            data-field="de4_arch_to_treat"
                            value="lower"
                            onChange={this.props.onInputChange_de4}
                            checked={['lower', 'both'].includes(this.props.de4_arch_to_treat)}
                            defaultChecked={this.props.prevArches.includes('lower')}
                          />{' '}
                          Lower arch - {this.props.de4_is_lower_smartwire_new ? 'Initial' : 'Additional'} Smartwires
                        </label>{' '}
                      </div>
                    </div>
                    <div className="col-md-12">
                      <AlertContainer>
                        <InfoBlueIcon />
                        Additional fees may be incurred
                      </AlertContainer>
                    </div>
                  </div>
                </CardContainer>
              </div>
            </div>
          </div>

          {/* 2. Current engaged Smartwire */}
          <div className="row">
            <div className="col-md-12">
              <div className="question-box" data-testid="de4_smartwire_box">
                <span className="title emphasis">
                  <span className="title-index emphasis">2.</span>
                  <span className="emphasis">Current engaged Smartwire</span>
                </span>

                <CardContainer type="form">
                  <div className="row">
                    <div className="col-md-6">
                      <label className="emphasis">Upper</label>
                      <Dropdown
                        options={upperSmartwireOptions}
                        defaultValue={upperSmartwireOptions.find((sw) => sw.value === 'none')}
                        value={upperSmartwireOptions.find((sw) => this.props.de4_upper_smartwire === sw.value)}
                        onChange={this.props.onInputChange_de4}
                        disabled={this.canDisableDropdown() && !this.props.prevArches.includes('upper')}
                      />
                    </div>
                    <div className="col-md-6">
                      {this.props.de4_upper_smartwire !== 'none' && (
                        <RadioDate
                          field="de4_upper_smartwire_date"
                          value={this.props.de4_upper_smartwire_date}
                          onChange={this.props.onInputChange_de4}
                          onFocus={this.props.onFocus}
                          warning={!isValidDate(this.props.de4_upper_smartwire_date)}
                        />
                      )}
                    </div>
                  </div>
                  <br />
                  <div className="row">
                    <div className="col-md-6">
                      <label className="emphasis">Lower</label>
                      <Dropdown
                        options={lowerSmartwireOptions}
                        defaultValue={lowerSmartwireOptions.find((sw) => sw.value === 'none')}
                        value={lowerSmartwireOptions.find((sw) => this.props.de4_lower_smartwire === sw.value)}
                        onChange={this.props.onInputChange_de4}
                        disabled={this.canDisableDropdown() && !this.props.prevArches.includes('lower')}
                      />
                    </div>
                    <div className="col-md-6">
                      {this.props.de4_lower_smartwire !== 'none' && (
                        <RadioDate
                          field="de4_lower_smartwire_date"
                          value={this.props.de4_lower_smartwire_date}
                          onChange={this.props.onInputChange_de4}
                          onFocus={this.props.onFocus}
                          warning={!isValidDate(this.props.de4_lower_smartwire_date)}
                        />
                      )}
                    </div>
                  </div>
                </CardContainer>
              </div>
            </div>
          </div>

          {/* 3. Missing teeth (Not including implant, bridge or primary tooth with agenesis)*/}
          <div className="row">
            <div className="col-md-12">
              <TeethArea
                headingIndex="3"
                heading="Missing teeth (Not including implant, bridge or primary tooth with agenesis)"
                field="de4_missing_teeth"
                teeth={this.props.de4_missing_teeth}
                onClick={this.props.onInputChange_de4}
                warning={
                  _.intersection(['UR7', 'UL7'], this.props.de4_missing_teeth).length >= 1 && this.props.hasUpper() ? (
                    <AlertContainer>Brackets on upper 7's will be added only if there is enough room on the lingual surface.</AlertContainer>
                  ) : null
                }
                hasUpper={this.props.hasUpper()}
                hasLower={this.props.hasLower()}
                teethReference={this.props.teethReference}
                dataTestId="de4_missing_teeth_box"
              />
            </div>
          </div>

          {/* 4. Additional teeth to be extracted */}
          <div className="row">
            <div className="col-md-12">
              <TeethArea
                headingIndex="4"
                heading="Additional teeth to be extracted"
                field="de4_extract_teeth"
                teeth={this.props.de4_extract_teeth}
                onClick={this.props.onInputChange_de4}
                warning={
                  _.intersection(['UR7', 'UL7'], this.props.de4_extract_teeth).length >= 1 && this.props.hasUpper() ? (
                    <AlertContainer>Brackets on upper 7's will be added only if there is enough room on the lingual surface.</AlertContainer>
                  ) : null
                }
                hasUpper={this.props.hasUpper()}
                hasLower={this.props.hasLower()}
                disabledTeeth={this.props.de4_missing_teeth}
                teethReference={this.props.teethReference}
                dataTestId="de4_extract_teeth_box"
              />
            </div>
          </div>

          {/* 5. Brackets to be included in new Smartwire design */}
          <div className="row">
            <div className="col-md-12">
              <TeethArea
                headingIndex="5"
                heading="Brackets to be included in new Smartwire design"
                headingMessage={this.getBracketsHeadingMessage()}
                footerNote="*If no changes, any missing bracket will be replaced with a single-tooth IDB tray."
                field="de4_bracket_teeth"
                teeth={this.props.de4_bracket_teeth}
                onClick={this.props.onInputChange_de4}
                warning={
                  _.intersection(['UR7', 'UL7'], this.props.de4_bracket_teeth).length >= 1 && this.props.hasUpper() ? (
                    <AlertContainer>Brackets on upper 7's will be added only if there is enough room on the lingual surface.</AlertContainer>
                  ) : null
                }
                hasUpper={this.props.hasUpper()}
                hasLower={this.props.hasLower()}
                disabledTeeth={[...this.props.de4_missing_teeth, ...this.props.de4_extract_teeth]}
                teethReference={this.props.teethReference}
                tset={tset}
                showPresets={true}
                presets={this.props.presets}
                minPreset={this.props.minPreset}
                maxPreset={this.props.maxPreset}
                de4_bracket_teeth={this.props.de4_bracket_teeth}
                setBracketTeeth={this.props.setBracketTeeth}
                dataTestId="de4_bracket_teeth_box"
                prevBrackets={this.props.prevBrackets}
              />
            </div>
          </div>

          {/* 6. Do not move the following teeth */}
          <div className="row">
            <div className="col-md-12">
              <TeethArea
                headingIndex="6"
                heading="Do not move the following teeth"
                sub_heading={this.getRestrictionMsg()}
                field="de4_teeth_restriction"
                teeth={this.props.de4_teeth_restriction}
                onClick={this.props.onInputChange_de4}
                hasUpper={this.props.hasUpper()}
                hasLower={this.props.hasLower()}
                disabledTeeth={[...this.props.de4_missing_teeth, ...this.props.de4_extract_teeth]}
                teethReference={this.props.teethReference}
                dataTestId="de4_teeth_restriction_box"
              />
            </div>
          </div>

          {/* 7. Anterior-Posterior */}
          <div className="row">
            <div className="col-md-12">
              <div className="question-box" data-testid="de4_anterior_posterior_box">
                <span id="de4_anterior_posterior" className="title emphasis">
                  <span className="title-index emphasis">7.</span>
                  Anterior-Posterior
                  {[this.props.de4_anterior_posterior_l, this.props.de4_anterior_posterior_r].includes('correction') ||
                  [this.props.de4_anterior_posterior_l, this.props.de4_anterior_posterior_r].includes('other')
                    ? '*'
                    : ''}
                </span>

                <CardContainer type="form">
                  <div className="anterior-posterior">
                    <div className="anterior-posterior__radios">
                      <div className="header">
                        <div className="header__c1"></div>
                        <div className="header__c2">R</div>
                        <div className="header__c3">L</div>
                      </div>

                      <div className="row1">
                        <div className="row1__c1">Maintain current</div>
                        <div className="row1__c2">
                          <div className="ib-radio">
                            <input
                              id="de4_anterior_posterior_r-current"
                              className="form-check-input"
                              type="radio"
                              name="de4_anterior_posterior_r"
                              data-field="de4_anterior_posterior_r"
                              value="current"
                              onChange={this.props.onInputChange_de4}
                              defaultChecked={this.props.de4_anterior_posterior_r === 'current'}
                            />
                          </div>
                        </div>
                        <div className="row1__c3">
                          <div className="ib-radio">
                            <input
                              id="de4_anterior_posterior_l-current"
                              className="form-check-input"
                              type="radio"
                              name="de4_anterior_posterior_l"
                              data-field="de4_anterior_posterior_l"
                              value="current"
                              onChange={this.props.onInputChange_de4}
                              defaultChecked={this.props.de4_anterior_posterior_l === 'current'}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="row2">
                        <div className="row2__c1">Correction to Class I</div>
                        <div className="row2__c2">
                          <div className="ib-radio">
                            <input
                              id="de4_anterior_posterior_r-correction"
                              className="form-check-input"
                              type="radio"
                              name="de4_anterior_posterior_r"
                              data-field="de4_anterior_posterior_r"
                              value="correction"
                              onChange={this.props.onInputChange_de4}
                              defaultChecked={this.props.de4_anterior_posterior_r === 'correction'}
                            />
                          </div>
                        </div>
                        <div className="row2__c3">
                          <div className="ib-radio">
                            <input
                              id="de4_anterior_posterior_l-correction"
                              className="form-check-input"
                              type="radio"
                              name="de4_anterior_posterior_l"
                              data-field="de4_anterior_posterior_l"
                              value="correction"
                              onChange={this.props.onInputChange_de4}
                              defaultChecked={this.props.de4_anterior_posterior_l === 'correction'}
                            />
                          </div>
                        </div>
                      </div>

                      {[this.props.de4_anterior_posterior_l, this.props.de4_anterior_posterior_r].includes('correction') ? (
                        <div className="anterior-posterior__corrections-checkboxes">
                          <div className="ib-checkbox">
                            <label className="form-check-label">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                name="class_II_or_III_correction_simulation"
                                id="class_II_or_III_correction_simulation"
                                data-field="de4_anterior_posterior_corrections"
                                checked={this.props.de4_anterior_posterior_corrections.class_II_or_III_correction_simulation}
                                onChange={this.props.onInputChange_de4}
                              />{' '}
                              {'Class II/III correction simulation (elastics or auxiliaries required)'}
                            </label>
                          </div>
                          <div className="ib-checkbox">
                            <label className="form-check-label">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                name="posterior_IPR"
                                id="posterior_IPR"
                                data-field="de4_anterior_posterior_corrections"
                                checked={this.props.de4_anterior_posterior_corrections.posterior_IPR}
                                onChange={this.props.onInputChange_de4}
                              />{' '}
                              {'Posterior IPR'}
                            </label>
                          </div>
                        </div>
                      ) : null}

                      <div className="row2">
                        <div className="row2__c1">Other (Please specify)</div>
                        <div className="row2__c2">
                          <div className="ib-radio">
                            <input
                              id="de4_anterior_posterior_r-other"
                              className="form-check-input"
                              type="radio"
                              name="de4_anterior_posterior_r"
                              data-field="de4_anterior_posterior_r"
                              value="other"
                              onChange={this.props.onInputChange_de4}
                              defaultChecked={this.props.de4_anterior_posterior_r === 'other'}
                            />
                          </div>
                        </div>
                        <div className="row2__c3">
                          <div className="ib-radio">
                            <input
                              id="de4_anterior_posterior_l-other"
                              className="form-check-input"
                              type="radio"
                              name="de4_anterior_posterior_l"
                              data-field="de4_anterior_posterior_l"
                              value="other"
                              onChange={this.props.onInputChange_de4}
                              defaultChecked={this.props.de4_anterior_posterior_l === 'other'}
                            />
                          </div>
                        </div>
                      </div>

                      {[this.props.de4_anterior_posterior_r, this.props.de4_anterior_posterior_l].includes('other') ? (
                        <div className="anterior-posterior__corrections-checkboxes">
                          <TextArea
                            rows="3"
                            type="text"
                            className="form-control inline-block fluid-size"
                            placeholder="Specify instructions for AP correction"
                            data-field="de4_anterior_posterior_notes"
                            id="de4_anterior_posterior_notes"
                            onChange={this.props.onInputChange_de4}
                            value={this.props.de4_anterior_posterior_notes}
                            onFocus={this.props.onFocus}
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                  {[this.props.de4_anterior_posterior_r, this.props.de4_anterior_posterior_l].includes('correction') && (
                    <AlertContainer>Auxiliaries will be needed. InSmile Smartwires cannot fully correct Class II or Class III malocclusion.</AlertContainer>
                  )}
                </CardContainer>
              </div>
            </div>
          </div>

          {/* 8. Allow InSmile Designers to idealize occlusion */}
          <div className="row">
            <div className="col-md-12">
              <div className="question-box" data-testid="de4_occlusion_box">
                <span id="de4_occlusion" className="title emphasis">
                  <span className="title-index emphasis">8.</span>
                  Allow InSmile Designers to idealize occlusion
                </span>

                <CardContainer type="form">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="ib-radio">
                        <label className="form-check-label ">
                          <input
                            id="de4_occlusion-allow"
                            className="form-check-input"
                            type="radio"
                            name="de4_occlusion"
                            data-field="de4_occlusion"
                            value="Yes"
                            onChange={this.props.onInputChange_de4}
                            checked={this.props.de4_occlusion === 'Yes'}
                          />{' '}
                          Yes
                        </label>{' '}
                      </div>
                      <div className="ib-radio">
                        <label className="form-check-label ">
                          <input
                            id="de4_occlusion-only"
                            className="form-check-input"
                            type="radio"
                            name="de4_occlusion"
                            data-field="de4_occlusion"
                            value="No"
                            onChange={this.props.onInputChange_de4}
                            checked={this.props.de4_occlusion === 'No'}
                          />{' '}
                          No, only move teeth as described below
                        </label>{' '}
                      </div>
                    </div>
                  </div>
                  {this.props.de4_occlusion === 'No' && (
                    <TextArea
                      rows="3"
                      type="text"
                      className="form-control inline-block fluid-size"
                      placeholder="Describe desired teeth movement"
                      data-field="de4_occlusion_notes"
                      id="de4_occlusion_notes"
                      onChange={this.props.onInputChange_de4}
                      value={this.props.de4_occlusion_notes}
                      onFocus={this.props.onFocus}
                    />
                  )}
                </CardContainer>
              </div>
            </div>
          </div>

          {/* 9. Any additional instructions (e.g. Midlines, IPR, etc.) */}
          <div className="row">
            <div className="col-md-12">
              <div className="question-box" data-testid="de4_additional_instructions_box">
                <span id="de4_additional_instructions" className="title emphasis">
                  <span className="title-index emphasis">9.</span>
                  Any additional instructions (e.g. Midlines, IPR, etc.)
                </span>

                <CardContainer type="form">
                  <div className="row">
                    <div className="col-md-12">
                      <TextArea
                        rows="3"
                        type="text"
                        className="form-control inline-block fluid-size"
                        placeholder="Specify remaining treatment goals and any additional instructions"
                        data-field="de4_additional_instructions"
                        id="de4_additional_instructions"
                        onChange={this.props.onInputChange_de4}
                        value={this.props.de4_additional_instructions}
                        onFocus={this.props.onFocus}
                      />
                    </div>
                  </div>
                </CardContainer>
              </div>
            </div>
          </div>
        </form>

        {this.props.record_states.length > 0 ? (
          <div className="col-md-4 tr-viewer-container-parent">
            <div className="tr-viewer-container">
              <RecordViewer
                records={this.props.record_states}
                onLoad={this.props.onLoadImageRecord}
                onClick={(id, action) => {
                  onImageToolAction(id, action, this.props);
                }}
              >
                <ToothViewer teethSets={tset} thumbs={this.state.thumbs} />
              </RecordViewer>
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    record_states: getRecordStates(state),
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      buildRecordStates: buildRecordStates,
      onLoadImageRecord: onLoadImageRecord,
      onRecordViewerAction: onRecordViewerAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(DigitalEnhancementForm4);
